import { Media } from "@/interfaces";
import { EnvValues } from "@/interfaces/customTypes/envValues";
import { FirebaseStorage, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { getFirebaseStorage, saveElementToFirebase } from "./firebase";
import { productMediaFirebaseUrl } from "./routes";
import { createVideoThumbnailBlob, dataURLtoBlob } from "./videoHelpers";
import { SocialCoachSession } from "@/services/bedrock";

class ProjectFileUploader {
  private readonly firebaseUrl: string;
  private readonly config: EnvValues;
  private readonly firebaseStorage: FirebaseStorage;

  constructor(
    config: EnvValues,
    session: SocialCoachSession | undefined,
    product: "videoProject" | "teleprompter" | "post" | "videoPage" | "playerCard" | "screenCapture",
    playerId?: number
  ) {
    let accountId: string | number;
    let accountType: "coach" | "player" | "user";
    if (playerId) {
      accountId = playerId.toString();
      accountType = "player";
    } else {
      switch (session?.userRole) {
        case "COACH":
        case "ASSISTANT_COACH":
          accountId = session.coachId || "default";
          accountType = "coach";
          break;
        case "PLAYER":
          accountId = session?.playerId || "default";
          accountType = "player";
          break;
        default:
          accountId = session?.userId || "default";
          accountType = "user";
      }
    }
    this.firebaseUrl = productMediaFirebaseUrl(product, accountId, accountType);
    this.config = config;
    this.firebaseStorage = getFirebaseStorage(this.config);
  }

  async uploadFile(file: File, fileName: string, onProgress?: (progress: number) => void): Promise<string> {
    const extension = file.name.includes(".") ? file.name.split(".").pop() : "";
    const destName =
      extension && !fileName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)
        ? `${fileName}.${extension}`
        : fileName;
    const destUri = `${this.firebaseConfig.baseImagesFolder}/${this.firebaseUrl}/${new Date().getTime()}/${destName}`;
    const storageRef = ref(this.firebaseStorage, destUri);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on("state_changed", snapshot => {
      onProgress?.(snapshot.bytesTransferred / snapshot.totalBytes);
    });

    await uploadTask;
    return getDownloadURL(storageRef);
  }

  async prepareMedia(mediaList: Media[], onUploadProgress: (fileId: number, progress: number) => void) {
    return Promise.all(
      mediaList.map(async (mediaInput): Promise<Media> => {
        let media: Media = {
          ...mediaInput,
          validAspectRatio: true,
          validDimension: true,
        };
        if (media.imageFile && !media.uri.startsWith("http")) {
          media = await this.uploadMediaToFirebase(media, onUploadProgress);
        }

        if (!media.thumbnailUri || media.thumbnailUri.length === 0) {
          if (media.category === "VIDEO") {
            media = await this.uploadThumbnailToFirebase(media);
          } else {
            media = {
              ...media,
              thumbnailUri: media.uri,
            };
          }
        }

        return media;
      })
    );
  }

  private generateDestUri(media: Media): string {
    const [type, format] = media.imageFile!!.type.split("/");

    if (!type) {
      throw Error("Invalid file type");
    }
    return `${this.config.firebaseFolder}/${this.firebaseUrl}/${new Date().getTime()}/${media.fileName}.${format}`;
  }

  private async uploadMediaToFirebase(
    media: Media,
    onUploadProgress: (fileId: number, progress: number) => void
  ): Promise<Media> {
    const destUri = this.generateDestUri(media);
    const storageRef = ref(this.firebaseStorage, destUri);
    const uploadTask = uploadBytesResumable(storageRef, media.imageFile!!);

    // set Firebase callback functions
    uploadTask.on("state_changed", snapshot => {
      const uploadProgress = snapshot.bytesTransferred / snapshot.totalBytes;
      onUploadProgress(media.id, uploadProgress);
    });

    await uploadTask;
    const uri = await getDownloadURL(storageRef);
    return {
      ...media,
      uri,
    };
  }

  private async uploadThumbnailToFirebase(media: Media): Promise<Media> {
    try {
      const thumbnail = await createVideoThumbnailBlob(media.uri);
      const blob = dataURLtoBlob(thumbnail);
      const thumbnailUri = await saveElementToFirebase(
        this.config,
        "thumbnail/" + media.fileName,
        blob,
        "thumbnail_" + media.fileName
      );
      return {
        ...media,
        thumbnailUri: thumbnailUri,
      };
    } catch (e) {
      console.error("Error creating thumbnail", e);
      return media;
    }
  }
}

export { ProjectFileUploader };
